import { CONTACT_OPTIONS, LIST_COUNTRIES } from '@rabbit/bizproc/react';
import {
  InputTypeSelectAddress,
  isValidPhoneNumber,
} from '@rabbit/elements/shared-components';
import { Input } from 'libs/elements/shared-components/src/lib/atoms/inputs/Input/Input';
import * as Yup from 'yup';
import {
  CreateNewConsumerForm_FieldNames,
  CreateNewConsumerForm_FieldNamesShape,
  CreateNewConsumerForm_FormValues,
} from './types';
import { t } from 'i18next';
import { useContext } from 'react';
import { AppContext } from '@rabbit/app-context';

// Configuration object with the common data and generators for the "Generic Flow" in the "ModalNewRegistration" component
// Arguably we would want a single flow using these configurations, but one step at a time
// This is used and overridden by the specific tenant configurations
// eg. ModalNewRegistration_Config_CharlieOven

const ModalNewRegistration_StepTitles_Generic = {
  0: t('New registration'),
  1: t('Create new user'),
  2: t('Complete registration'),
  3: t('Summary'),
  4: t('Consumer details'),
  5: t('Summary'),
};

/* -------------------------------------------------------------------------- */
/*                            CreateNewConsumerForm                           */
/* -------------------------------------------------------------------------- */

/* ---------------------------- Validation Schema --------------------------- */
const CNCF_ValidationSchema_Generic = Yup.object().shape({
  [CreateNewConsumerForm_FieldNames.consumer_first_name]: Yup.string()
    .required(t('Please enter a first name'))
    .matches(
      /^(?:[A-Z][a-z]*['´`-]?\s?){1,2}|^[A-Z][a-z]*$/,
      'First name must start with a capital letter and contain only letters.'
    ),
  [CreateNewConsumerForm_FieldNames.consumer_last_name]: Yup.string()
    .trim()
    .required(t('Please enter a last name'))
    .matches(
      /^(?:[A-Z][a-z]*['´`-]?\s?){1,2}|^[A-Z][a-z]*$/,
      'Last name must start with a capital letter and contain only letters.'
    ),
  [CreateNewConsumerForm_FieldNames.consumer_email]: Yup.string()
    .email(t('Please enter a valid email address'))
    .required(t("Email can't be empty")),
  [CreateNewConsumerForm_FieldNames.consumer_telephone]: Yup.string()
    .min(6, t('Phone number must be at least 6 characters'))
    .required(t('Please enter a telephone'))
    .test('is-valid-phone', t('Invalid phone number'), function (value) {
      const parent: any = this.parent || {};
      return isValidPhoneNumber(parent.consumer_telephone_e164 || value);
    }),
  [CreateNewConsumerForm_FieldNames.consumer_preferred_contact]: Yup.array()
    .min(1, t('Please select at least one preferred contact method'))
    .typeError(t('Please select a preferred contact method'))
    .required(t('Please select a preferred contact method')),
  [CreateNewConsumerForm_FieldNames.consumer_address]: Yup.mixed(),
});

export const CNCF_ADDRESS_VALIDATION_SCHEMA = Yup.object().shape({
  [CreateNewConsumerForm_FieldNames.consumer_address]: Yup.object().shape({
    address_line_1: Yup.string()
      .trim()
      .required('Please enter an address line'),
    address_line_2: Yup.string().trim(),
    town: Yup.string().trim().required('Please enter a town'),
    state: Yup.string().trim().required('Please enter a state'),
    postcode: Yup.string().trim().required('Please enter a postcode'),
    country: Yup.string().trim().required('Please select a country'),
    isDefault: Yup.boolean().optional(),
  }),
});

/* ------------------- Generate initial values for Formik ------------------- */
function CNCF_GenerateInitialValues_Generic(
  params: Partial<CreateNewConsumerForm_FormValues>
) {
  return {
    [CreateNewConsumerForm_FieldNames.consumer_first_name]:
      params.consumer_first_name ?? '',
    [CreateNewConsumerForm_FieldNames.consumer_last_name]:
      params.consumer_last_name ?? '',
    [CreateNewConsumerForm_FieldNames.consumer_email]:
      params.consumer_email ?? '',
    [CreateNewConsumerForm_FieldNames.consumer_telephone]:
      params.consumer_telephone ?? '',
    [CreateNewConsumerForm_FieldNames.consumer_preferred_contact]:
      params.consumer_preferred_contact ?? [],
    [CreateNewConsumerForm_FieldNames.consumer_address]:
      params.consumer_address ?? {
        line1: '',
        line2: '',
        town: '',
        state: '',
        postcode: '',
        country: '',
      },
  };
}

function CNCF_GenerateInput_Generic(
  input: CreateNewConsumerForm_FieldNamesShape,
  props?: { [key: string]: any }
) {
  const { tenantInfo } = useContext(AppContext);
  switch (input) {
    case CreateNewConsumerForm_FieldNames.consumer_first_name:
      return (
        <Input
          type="text"
          name={CreateNewConsumerForm_FieldNames.consumer_first_name}
          label={t('First name*')}
          settings={{
            id: CreateNewConsumerForm_FieldNames.consumer_first_name,
            placeholder: t('First name'),
            hint: t('*required'),
          }}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_last_name:
      return (
        <Input
          type="text"
          name={CreateNewConsumerForm_FieldNames.consumer_last_name}
          label={t('Last name*')}
          settings={{
            id: CreateNewConsumerForm_FieldNames.consumer_last_name,
            placeholder: t('Last name'),
            hint: t('*required'),
          }}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_email:
      return (
        <Input
          type="email"
          name={CreateNewConsumerForm_FieldNames.consumer_email}
          label={t('Email*')}
          settings={{
            id: CreateNewConsumerForm_FieldNames.consumer_email,
            placeholder: t('example@example.com'),
            hint: t('*required'),
          }}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_telephone:
      return (
        <Input
          type="phone"
          name={CreateNewConsumerForm_FieldNames.consumer_telephone}
          label={t('Phone number*')}
          settings={{
            id: CreateNewConsumerForm_FieldNames.consumer_telephone,
            placeholder: t('Phone number'),
            hint: t('*required'),
          }}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_preferred_contact:
      return (
        <Input
          type="select"
          name={CreateNewConsumerForm_FieldNames.consumer_preferred_contact}
          label={t('Preferred contact method*')}
          settings={{
            isMulti: true,
            id: CreateNewConsumerForm_FieldNames.consumer_preferred_contact,
            placeholder: t('Please select'),
            options: CONTACT_OPTIONS,
            errors: props?.errors,
            hint: t('*required'),
          }}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address_country:
      return (
        <Input
          type="select"
          label={t('Country')}
          name={CreateNewConsumerForm_FieldNames.consumer_address_country}
          settings={{
            options: LIST_COUNTRIES,
          }}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address:
      return (
        <InputTypeSelectAddress
          //onChange={(address) => setSelectedAddress(address)}
          onChange={() => void 0}
          errors={''}
          country={''}
          // errors={props.errors['consumer_address']}
          // country={props.values?.consumer_address?.country}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address_line1:
      return (
        <Input
          type="text"
          label={t('Address line')}
          name={CreateNewConsumerForm_FieldNames.consumer_address_line1}
          settings={{}}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address_line2:
      return (
        <Input
          type="text"
          label={t('Address line 2')}
          name={CreateNewConsumerForm_FieldNames.consumer_address_line2}
          settings={{}}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address_town:
      return (
        <Input
          type="text"
          label={t('City/Suburb')}
          name={CreateNewConsumerForm_FieldNames.consumer_address_town}
          settings={{}}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address_state:
      return (
        <Input
          type="text"
          label={t('State/Territory')}
          name={CreateNewConsumerForm_FieldNames.consumer_address_state}
          settings={{}}
          {...props}
        />
      );
    case CreateNewConsumerForm_FieldNames.consumer_address_postcode:
      return (
        <Input
          type="text"
          label={
            tenantInfo?.country === 'US'
              ? t('general.zipCode')
              : t('general.postCode')
          }
          name={CreateNewConsumerForm_FieldNames.consumer_address_postcode}
          settings={{
            id: 'zip_code',
            placeholder: '',
          }}
          {...props}
        />
      );
  }
}

const CreateNewConsumerForm = {
  validationSchema: CNCF_ValidationSchema_Generic,
  generateInitialValues: CNCF_GenerateInitialValues_Generic,
  generateInput: CNCF_GenerateInput_Generic,
};
/* -------------------------------------------------------------------------- */
/*                                Config Objects                              */
/* -------------------------------------------------------------------------- */

export interface ModalNewRegistration_ConfigShape_Generic {
  StepTitles: typeof ModalNewRegistration_StepTitles_Generic;
  CreateNewConsumerForm: typeof CreateNewConsumerForm;
}

export const ModalNewRegistration_Config = {
  StepTitles: ModalNewRegistration_StepTitles_Generic,
  CreateNewConsumerForm,
};
